import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@apps/web/lib/utils';

type MarkdownAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    readonly openInNewTab?: boolean;
};

// Shared anchor for every markdown surface (defaults below plus the
// chat renderer's reference-aware fallback) so link affordance never
// drifts between surfaces. The theme has no link token and `primary`
// is near-foreground, so links need their own hue to be identifiable
// as clickable in running text. External links always open in a new
// tab; `openInNewTab` additionally covers relative/internal hrefs.
export function MarkdownAnchor({
    href,
    openInNewTab = false,
    children,
    className,
    ...props
}: MarkdownAnchorProps) {
    // Case-insensitive scheme, plus protocol-relative (`//host`) URLs,
    // all of which navigate cross-origin and must open in a new tab.
    const isExternal = /^(?:https?:)?\/\//iu.test(href ?? '');
    const opensNewTab = isExternal || openInNewTab;
    return (
        <a
            href={href}
            className={cn(
                'text-blue-600 underline decoration-blue-600/40 underline-offset-2 transition-colors',
                'hover:text-blue-700 hover:decoration-blue-700',
                'dark:text-blue-400 dark:decoration-blue-400/40 dark:hover:text-blue-300 dark:hover:decoration-blue-300',
                opensNewTab &&
                    "after:ml-0.5 after:align-super after:text-[0.65em] after:content-['↗']",
                className,
            )}
            {...props}
            {...(opensNewTab
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
        >
            {children}
            {opensNewTab && (
                <span className="sr-only"> (opens in a new tab)</span>
            )}
        </a>
    );
}

// Only Phoebe's public asset/doc hosts get the auto-loading <video>
// element and act as poster sources. Any other URL in agent-rendered
// markdown falls through to the normal image path, so untrusted content
// can't make the browser fetch media from an arbitrary host (which would
// leak the viewer's IP and, for <video>, has no referrer protection).
const TRUSTED_MEDIA_HOSTS = new Set(['assets.phoebe.work', 'docs.phoebe.work']);

function isTrustedMediaUrl(url: string | undefined): boolean {
    if (url === undefined) {
        return false;
    }
    try {
        return TRUSTED_MEDIA_HOSTS.has(new URL(url).hostname);
    } catch {
        return false;
    }
}

export const defaultMarkdownComponents: Components = {
    h1: ({ node, ...props }) => (
        <h1 className="my-2 text-base font-semibold" {...props} />
    ),
    h2: ({ node, ...props }) => (
        <h2 className="my-2 text-sm font-semibold" {...props} />
    ),
    h3: ({ node, ...props }) => (
        <h3 className="my-1 text-sm font-medium" {...props} />
    ),
    p: ({ node, ...props }) => (
        <p className="my-1 text-sm leading-relaxed break-words" {...props} />
    ),
    strong: ({ node, ...props }) => (
        <strong className="font-semibold" {...props} />
    ),
    em: ({ node, ...props }) => <em className="italic" {...props} />,
    a: ({ node, ...props }) => <MarkdownAnchor {...props} />,
    img: ({ node, src, alt, title, ...props }) => {
        // Docs embed screen recordings as markdown images pointing at
        // video files; play those with a native player. The markdown
        // title carries the poster thumbnail URL when the docs provide
        // one. Video and poster are gated to trusted media hosts because
        // <video> has no referrerpolicy attribute to fall back on.
        const mediaClassName =
            'border-border my-2 h-auto max-h-96 max-w-[80%] rounded-md border';
        if (
            typeof src === 'string' &&
            /\.(mp4|webm|mov)(?:[?#]|$)/iu.test(src) &&
            isTrustedMediaUrl(src)
        ) {
            const poster =
                title !== undefined && isTrustedMediaUrl(title)
                    ? title
                    : undefined;
            // Without a poster, a media fragment makes the browser
            // decode and show a real frame instead of a blank box.
            const videoSrc = poster || src.includes('#') ? src : `${src}#t=0.1`;
            // `props` is typed as img attributes (loading, srcSet, …) that
            // are not valid on <video>, so it is intentionally not spread
            // here; the docs media set carries no extra attributes anyway.
            return (
                <video
                    src={videoSrc}
                    poster={poster}
                    controls={true}
                    playsInline={true}
                    preload="metadata"
                    aria-label={alt}
                    className={mediaClassName}
                />
            );
        }
        return (
            <img
                {...props}
                src={src}
                alt={alt}
                title={title}
                className={mediaClassName}
                loading="lazy"
                referrerPolicy="no-referrer"
            />
        );
    },
    ul: ({ node, ...props }) => (
        <ul className="my-1 list-disc space-y-0.5 pl-4" {...props} />
    ),
    ol: ({ node, ...props }) => (
        // pl-6 (not pl-4): the number markers render in the padding, and at
        // pl-4 the marker + gap overflow left of the row, where the chat's
        // overflow-x-hidden clips the left edge of each number.
        <ol className="my-1 list-decimal space-y-0.5 pl-6" {...props} />
    ),
    li: ({ node, ...props }) => (
        <li className="text-sm leading-relaxed break-words" {...props} />
    ),
    code: ({ node, ...props }) => (
        <code className="bg-muted rounded px-1 py-0.5 text-xs" {...props} />
    ),
    // w-0 + min-w-full on the scroll wrappers zeroes their intrinsic
    // width contribution while still rendering at full width: without it,
    // a wide table or long code line propagates its min-content width
    // through overflow-x-auto and inflates intrinsically-sized ancestors
    // (e.g. the ScrollArea viewport's display:table wrapper), pushing the
    // whole chat transcript past the panel edge instead of scrolling.
    pre: ({ node, ...props }) => (
        <pre
            className="bg-muted my-2 w-0 min-w-full overflow-x-auto rounded p-2 text-xs"
            {...props}
        />
    ),
    // w-max + per-cell max-w keeps columns readable in narrow panels:
    // with a plain w-full table, auto layout hands a prose column its
    // min-content width (one word per line) once nowrap neighbors eat
    // the available space. Sizing to max-content capped at max-w-64 per
    // cell lets text columns wrap at a readable measure while the
    // wrapper scrolls horizontally.
    table: ({ node, ...props }) => (
        <div className="my-3 w-0 min-w-full overflow-x-auto">
            <table
                className="w-max min-w-full border-collapse text-sm"
                {...props}
            />
        </div>
    ),
    thead: ({ node, ...props }) => <thead className="bg-muted/50" {...props} />,
    tr: ({ node, ...props }) => (
        <tr className="border-border border-b" {...props} />
    ),
    th: ({ node, ...props }) => (
        <th
            className="max-w-64 px-3 py-2 text-left text-xs font-medium break-words"
            {...props}
        />
    ),
    td: ({ node, ...props }) => (
        <td
            className="max-w-64 px-3 py-2.5 align-top text-sm leading-relaxed break-words"
            {...props}
        />
    ),
};

type MarkdownProps = {
    readonly children: string;
    readonly className?: string;
    readonly components?: Components;
} & Omit<
    React.ComponentProps<typeof ReactMarkdown>,
    'children' | 'className' | 'components'
>;

export function Markdown({
    children,
    className,
    components,
    ...props
}: MarkdownProps) {
    return (
        <div className={cn('space-y-1 break-words', className)}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    ...defaultMarkdownComponents,
                    ...components,
                }}
                {...props}
            >
                {children}
            </ReactMarkdown>
        </div>
    );
}
